import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  doc,
  updateDoc,
  collection,
  addDoc,
  deleteDoc,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import MenuTemplate1 from "../menus/menud1";
import MenuTemplate2 from "../menus/menud2";
import MenuTemplate3 from "../menus/menud3";
import MenuTemplate4 from "../menus/menud4";
import MenuTemplate5 from "../menus/menud5";
import MenuTemplate6 from "../menus/menud6";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";
import "../css/edit.css";

export default function Edit() {
  const { state } = useLocation();
  const nav = useNavigate();
  const menu = state?.menu;
  const level = state?.level;
  const [menuData, setMenuData] = useState({
    name: menu.name,
    tagline: menu.tagline || "",
    imageUrl: menu.imageUrl || null,
    showImages: menu.showImages ?? true,
    colors: menu.colors ?? {
      primary: "#2e7d32",
      background: "#fafafa",
      text: "#222",
      card: "#ffffff",
    },
    style: menu.style || "style1",
  });

  const [categories, setCategories] = useState([]);
  const [newCatName, setNewCatName] = useState("");

  const [newItem, setNewItem] = useState({
    name: "",
    description: "",
    price: "",
  });

  const [activeCat, setActiveCat] = useState(null);
  const [saving, setSaving] = useState(false);

  // -----------------------------
  // LOAD CATEGORIES + ITEMS
  // -----------------------------
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    const catRef = collection(db, "menus", menu.id, "categories");
    const catSnap = await getDocs(catRef);

    const cats = await Promise.all(
      catSnap.docs.map(async (catDoc) => {
        const itemsSnap = await getDocs(
          collection(db, "menus", menu.id, "categories", catDoc.id, "items")
        );

        return {
          id: catDoc.id,
          ...catDoc.data(),
          items: itemsSnap.docs
            .map((i) => ({ id: i.id, ...i.data() }))
            .sort((a, b) => a.order - b.order),
        };
      })
    );

    cats.sort((a, b) => a.order - b.order);
    setCategories(cats);
    setActiveCat(cats[0]?.id || null);
  };

  // -----------------------------
  // REORDER HELPERS
  // -----------------------------
  const moveCategory = (index, direction) => {
    if (index + direction < 0 || index + direction >= categories.length) return;

    const newCats = [...categories];
    const temp = newCats[index];
    newCats[index] = newCats[index + direction];
    newCats[index + direction] = temp;

    newCats.forEach((c, i) => (c.order = i));
    setCategories([...newCats]);
  };

  const moveItem = (catId, index, direction) => {
    const updatedCats = categories.map((cat) => {
      if (cat.id !== catId) return cat;

      const items = [...cat.items];
      if (index + direction < 0 || index + direction >= items.length) return cat;

      const temp = items[index];
      items[index] = items[index + direction];
      items[index + direction] = temp;

      items.forEach((i, idx) => (i.order = idx));

      return { ...cat, items };
    });

    setCategories(updatedCats);
  };

  // -----------------------------
  // IMAGE HANDLING
  // -----------------------------
  const uploadHeader = (file) => {
    const fileRef = ref(storage, `menus/${menu.id}/header/${file.name}`);
    const task = uploadBytesResumable(fileRef, file);

    task.on(
      "state_changed",
      () => {},
      (err) => console.error(err),
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setMenuData((p) => ({ ...p, imageUrl: url }));
        await updateDoc(doc(db, "menus", menu.id), { imageUrl: url });
      }
    );
  };

  const removeHeader = async () => {
    if (!menuData.imageUrl) return;
    try {
      const fileRef = ref(storage, menuData.imageUrl);
      await deleteObject(fileRef).catch(() => {});
      await updateDoc(doc(db, "menus", menu.id), { imageUrl: null });
      setMenuData((p) => ({ ...p, imageUrl: null }));
    } catch (e) {
      console.error(e);
    }
  };

  const uploadItemImg = (file, catId, itemId) => {
    const fileRef = ref(
      storage,
      `menus/${menu.id}/${catId}/items/${itemId}/${file.name}`
    );

    const task = uploadBytesResumable(fileRef, file);

    task.on(
      "state_changed",
      () => {},
      (err) => console.error(err),
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);

        setCategories((prev) =>
          prev.map((c) =>
            c.id === catId
              ? {
                  ...c,
                  items: c.items.map((i) =>
                    i.id === itemId ? { ...i, imageUrl: url } : i
                  ),
                }
              : c
          )
        );

        await updateDoc(
          doc(db, "menus", menu.id, "categories", catId, "items", itemId),
          { imageUrl: url }
        );
      }
    );
  };

  const removeItemImg = async (catId, itemId, url) => {
    try {
      const imgRef = ref(storage, url);
      await deleteObject(imgRef).catch(() => {});
    } catch {}
    await updateDoc(
      doc(db, "menus", menu.id, "categories", catId, "items", itemId),
      { imageUrl: null }
    );

    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? {
              ...c,
              items: c.items.map((i) =>
                i.id === itemId ? { ...i, imageUrl: null } : i
              ),
            }
          : c
      )
    );
  };

  // -----------------------------
  // ADD / REMOVE CATEGORY
  // -----------------------------
  const addCategory = async () => {
    if (!newCatName.trim()) return;

    const newDoc = await addDoc(
      collection(db, "menus", menu.id, "categories"),
      {
        name: newCatName,
        order: categories.length,
      }
    );

    setNewCatName("");
    loadCategories();
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm("Delete category?")) return;

    await deleteDoc(doc(db, "menus", menu.id, "categories", catId));
    loadCategories();
  };

  // -----------------------------
  // ADD / REMOVE ITEM
  // -----------------------------
  const addItem = async (catId) => {
    if (!newItem.name.trim() || !newItem.price.trim()) return;

    await addDoc(
      collection(db, "menus", menu.id, "categories", catId, "items"),
      {
        name: newItem.name,
        description: newItem.description,
        price: parseFloat(newItem.price),
        order: categories.find((c) => c.id === catId).items.length,
        imageUrl: null,
      }
    );

    setNewItem({ name: "", description: "", price: "" });
    loadCategories();
  };

  const deleteItem = async (catId, itemId) => {
    if (!window.confirm("Delete item?")) return;
    await deleteDoc(
      doc(db, "menus", menu.id, "categories", catId, "items", itemId)
    );
    loadCategories();
  };

  // -----------------------------
  // SAVE BUTTON
  // -----------------------------
  const saveAll = async () => {
    setSaving(true);

    const batch = writeBatch(db);

    // Save menu meta
    batch.update(doc(db, "menus", menu.id), {
      ...menuData,
      updatedAt: Date.now(),
    });

    // Save categories + items
    categories.forEach((cat, i) => {
      const refCat = doc(db, "menus", menu.id, "categories", cat.id);
      batch.update(refCat, { name: cat.name, order: i });

      cat.items.forEach((item, idx) => {
        const refItem = doc(
          db,
          "menus",
          menu.id,
          "categories",
          cat.id,
          "items",
          item.id
        );

        batch.update(refItem, {
          name: item.name,
          description: item.description,
          price: item.price,
          order: idx,
          imageUrl: item.imageUrl || null,
        });
      });
    });

    await batch.commit();
    setSaving(false);
    alert("Changes Saved!");
  };

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <div className="editPage">
      {/* LEFT PANEL: Editor */}
      <div className="editorPanel">
        <button style={{backgroundColor: "#2e7d32", border: "none", color: "white", borderRadius: 10, padding: 5}} onClick={() => nav("/dash")}>Back</button>
        <h2>Menu Editor</h2>

        {/* Menu Name */}
        <label>Name</label>
        <input
          value={menuData.name}
          onChange={(e) =>
            setMenuData((p) => ({ ...p, name: e.target.value }))
          }
        />

        {/* Tagline */}
        <label>Tagline</label>
        <input
          value={menuData.tagline}
          onChange={(e) =>
            setMenuData((p) => ({ ...p, tagline: e.target.value }))
          }
        />
    {(level == "basic")? (
        <></>
    ): (
        <>
        {/* Style Selector */}
            <label>Menu Style</label>
            <select
            value={menuData.style}
            onChange={(e) =>
                setMenuData((p) => ({ ...p, style: e.target.value }))
            }
            >
            <option value="style1">Style 1 (Clean)</option>
            <option value="style2">Style 2 (Modern)</option>
            <option value="style3">Style 3 (Simple)</option>
            <option value="style4">Style 4 (Modern Minimal)</option>
            <option value="style5">Style 5 (Modern Professional)</option>
            <option value="style6">Style 6 (Modern Wide)</option>
            </select>
            <p style={{fontSize: "8pt"}}>Not seeing a style that you like contact us so we can find somthing that will work</p>

        {/* Header Image */}
        <label>Header Image</label>
        {menuData.imageUrl ? (
          <div className="headerPreview">
            <img src={menuData.imageUrl} />
            <button onClick={removeHeader}>Remove</button>
          </div>
        ) : (
          <button>
            <input
              type="file"
              onChange={(e) => uploadHeader(e.target.files[0])}
            />
          </button>
        )}
    </>)}
        {/* Colors */}
        <h3>Colors</h3>
        {Object.keys(menuData.colors).map((key) => (
          <div className="colorRow" key={key}>
            <label>{key}</label>
            <input
              type="color"
              value={menuData.colors[key]}
              onChange={(e) =>
                setMenuData((p) => ({
                  ...p,
                  colors: { ...p.colors, [key]: e.target.value },
                }))
              }
            />
          </div>
        ))}

        {/* Categories */}
        <h3>Categories</h3>

        {categories.map((cat, idx) => (
          <div key={cat.id} className="categoryBlock">
            <div className="catRow">
              <input
                value={cat.name}
                onChange={(e) => {
                  const clone = [...categories];
                  clone[idx].name = e.target.value;
                  setCategories(clone);
                }}
              />

              <button onClick={() => moveCategory(idx, -1)}>↑</button>
              <button onClick={() => moveCategory(idx, 1)}>↓</button>
              <button onClick={() => deleteCategory(cat.id)}>✕</button>
            </div>

            {/* Items inside category */}
            {cat.items.map((item, i) => (
              <div key={item.id} className="itemRow">
                <input
                  value={item.name}
                  placeholder="Item name"
                  onChange={(e) => {
                    const clone = [...categories];
                    clone[idx].items[i].name = e.target.value;
                    setCategories(clone);
                  }}
                />
                <input
                  value={item.price}
                  placeholder="Price"
                  onChange={(e) => {
                    const clone = [...categories];
                    clone[idx].items[i].price = e.target.value;
                    setCategories(clone);
                  }}
                />

                <button onClick={() => moveItem(cat.id, i, -1)}>↑</button>
                <button onClick={() => moveItem(cat.id, i, 1)}>↓</button>
                {(level == "basic")?
                (<></>):(<>
                {item.imageUrl ? (
                  <button onClick={() => removeItemImg(cat.id, item.id, item.imageUrl)}>
                    Remove Img
                  </button>
                ) : (
                  <input
                    type="file"
                    onChange={(e) =>
                      uploadItemImg(e.target.files[0], cat.id, item.id)
                    }
                  />
                )}
                </>
                )}
                <button onClick={() => deleteItem(cat.id, item.id)}>✕</button>
              </div>
            ))}
            
            

            {/* Add item */}
            <div className="addItemRow">
              <input
                placeholder="Name"
                value={newItem.name}
                onChange={(e) =>
                  setNewItem((p) => ({ ...p, name: e.target.value }))
                }
              />
              <input
                placeholder="Description"
                type="text"
                value={newItem.description}
                onChange={(e) =>
                  setNewItem((p) => ({ ...p, description: e.target.value }))
                }
              />
              <input
                placeholder="Price"
                type="number"
                value={newItem.price}
                onChange={(e) =>
                  setNewItem((p) => ({ ...p, price: e.target.value }))
                }
              />
              <button onClick={() => addItem(cat.id)}>+ Item</button>
            </div>
          </div>
        ))}

        {/* Add new category */}
        <div className="addCatRow">
          <input
            placeholder="New category"
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
          />
          <button onClick={addCategory}>+ Category</button>
        </div>

        {/* SAVE BUTTON */}
        <button className="saveBtn" onClick={saveAll} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {/* RIGHT PANEL: LIVE PREVIEW */}
      <div className="previewPanel">

      {menuData.style === "style1" && (     
        <MenuTemplate1
        menu={{
        ...menuData,
        colors: menuData.colors,
        imageUrl:  menuData.imageUrl,
        }}
        categories={categories}
        />
        )}
        {menuData.style === "style2" && (    
        <MenuTemplate2
        menu={{
        ...menuData,
        colors: menuData.colors,
        imageUrl:  menuData.imageUrl,
        }}
        categories={categories}
        />
        )}
        {menuData.style === "style3" && (    
        <MenuTemplate3
        menu={{
        ...menuData,
        colors: menuData.colors,
        imageUrl:  menuData.imageUrl,
        }}
        categories={categories}
        />
        )}
        {menuData.style === "style4" && (    
        <MenuTemplate4
        menu={{
        ...menuData,
        colors: menuData.colors,
        imageUrl:  menuData.imageUrl,
        }}
        categories={categories}
        />
        )}
        {menuData.style === "style5" && (    
        <MenuTemplate5
        menu={{
        ...menuData,
        colors: menuData.colors,
        imageUrl:  menuData.imageUrl,
        }}
        categories={categories}
        />
        )}
        {menuData.style === "style6" && (    
        <MenuTemplate6
        menu={{
        ...menuData,
        colors: menuData.colors,
        imageUrl:  menuData.imageUrl,
        }}
        categories={categories}
        />
        )}
      </div>
    </div>
  );
}
